/**
 * Gestão de leads: listagem, stage, save e delete (histórico + chat + tracking).
 */
import React, { useState, useCallback } from 'react';
import { Lead, FunnelStage } from '../../../types';
import { ErrorHandler } from '../../../core/errors/ErrorHandler';
import { useLeadHistory } from './useLeadHistory';
import { notify } from '../../../utils/toast';
import { syncLeadWithChat } from '../../../services/modules/chatService';
import { assignmentSyncService } from '../../../services/AssignmentSyncService';
import { ReopenLeadNotification } from '../../../components/shared/ReopenLeadNotification';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { resolveLeadAgentForVisibility } from '../../../utils/leadAgentForVisibility';
import {
    createLeadUseCase,
    updateLeadUseCase,
    deleteLeadUseCase,
    getLeadsUseCase,
} from '../../../infrastructure/di/crmContainer';
import { mapDomainLeadToFrontend } from '../utils/leadMappers';
import { handleWonLeadTracking } from '../utils/trackingHelpers';
import { invalidateEnrichmentCache } from '../../../services/repositories/helpers/ConversationEnrichmentHelper';

export interface SaveLeadOptions {
    refreshFunnelId?: string | null;
}

function trackWonIfNeeded(lead: Lead, organizationId?: string) {
    if (lead.status === 'won' && organizationId) {
        void handleWonLeadTracking(lead, organizationId);
    }
}

export const useLeadManagement = (
    organizationId: string | undefined,
    selectedFunnelId: string | null,
    funnelStages: FunnelStage[],
    /** Dashboard/relatórios: todas as negociações da org */
    fetchAllLeads = false
) => {
    const [leads, setLeads] = useState<Lead[]>([]);
    const { user } = useAuth();
    const { canViewAllLeads } = usePermissions();
    const { updateCache, recordChanges, recordCreation } = useLeadHistory(funnelStages, organizationId);

    const syncLeadToChat = useCallback(async (lead: Lead) => {
        if (organizationId) await syncLeadWithChat(lead, organizationId);
    }, [organizationId]);

    const syncAgentToContactAndChat = useCallback(async (lead: Lead) => {
        if (!organizationId || !lead.whatsApp) return;
        const phone = lead.whatsApp.replace(/\D/g, '');
        if (!phone) return;
        await assignmentSyncService.syncByPhone(
            phone,
            lead.agent || null,
            organizationId,
            'opportunity',
            lead.id
        );
    }, [organizationId]);

    const afterLeadUpdated = useCallback((updatedLead: Lead) => {
        recordChanges(updatedLead);
        void syncLeadToChat(updatedLead);
    }, [recordChanges, syncLeadToChat]);

    const refreshLeads = useCallback(async (funnelIdOverride?: string | null) => {
        try {
            const idToUse = funnelIdOverride !== undefined
                ? funnelIdOverride
                : (fetchAllLeads ? null : selectedFunnelId);
            const domainLeads = await getLeadsUseCase.execute(idToUse || undefined, organizationId);
            setLeads(
                domainLeads
                    .filter(l => l.name && l.name.trim().length >= 2)
                    .map(lead => mapDomainLeadToFrontend(lead, updateCache))
            );
        } catch (error) {
            ErrorHandler.handle(error as Error, 'RefreshLeads');
        }
    }, [selectedFunnelId, organizationId, updateCache, fetchAllLeads]);

    const persistStageChange = useCallback(async (
        leadId: string,
        currentLead: Lead | undefined,
        optimisticPatch: Partial<Lead>,
        historyPatch: Partial<Lead>,
        payload: { id: string; stage: string; status: NonNullable<Lead['status']> },
        onSuccess?: (updatedLead: Lead) => void
    ) => {
        setLeads(prev => prev.map(l => (l.id === leadId ? { ...l, ...optimisticPatch } : l)));
        try {
            await updateLeadUseCase.execute({ ...payload, organizationId });
            if (currentLead) {
                const updatedLead = { ...currentLead, ...historyPatch };
                afterLeadUpdated(updatedLead);
                onSuccess?.(updatedLead);
            }
            await refreshLeads();
        } catch (error) {
            ErrorHandler.handle(error as Error, 'UpdateLeadStage');
            await refreshLeads();
        }
    }, [afterLeadUpdated, refreshLeads, organizationId]);

    const updateLeadStage = useCallback(async (leadId: string, newStageId: string) => {
        const currentLead = leads.find(l => l.id === leadId);

        if (newStageId === 'won' || newStageId === 'lost') {
            if (newStageId === 'won' && currentLead && !currentLead.contactId) {
                notify.error('Vincule um contato à negociação antes de marcá-la como ganha.');
                return;
            }
            const status = newStageId as 'won' | 'lost';
            const patch = { status, stage: currentLead?.stage ?? '' };
            await persistStageChange(
                leadId,
                currentLead,
                patch,
                patch,
                { id: leadId, ...patch },
                (updated) => trackWonIfNeeded(updated, organizationId)
            );
            return;
        }

        const executeActiveStageUpdate = async () => {
            const historyPatch = { stage: newStageId, status: 'open' as const };
            await persistStageChange(
                leadId,
                currentLead,
                { ...historyPatch, stageEntryDate: new Date().toISOString() },
                historyPatch,
                { id: leadId, ...historyPatch },
                () => {
                    if (currentLead?.status === 'won' || currentLead?.status === 'lost') {
                        notify.success('Negociação reaberta com sucesso');
                    }
                }
            );
        };

        if (currentLead && (currentLead.status === 'won' || currentLead.status === 'lost')) {
            notify.custom((t) => (
                <ReopenLeadNotification
                    leadName={currentLead.name}
                    t={t}
                    onConfirm={() => {
                        notify.dismiss(t);
                        void executeActiveStageUpdate();
                    }}
                    onCancel={() => notify.dismiss(t)}
                />
            ), { duration: 5000 });
            return;
        }

        await executeActiveStageUpdate();
    }, [leads, persistStageChange, organizationId]);

    const saveLead = useCallback(async (leadToSave: Lead, meta?: SaveLeadOptions): Promise<Lead | null> => {
        try {
            const isNew = !leadToSave.id || leadToSave.id.startsWith('temp_');
            const leadPersist: Lead = {
                ...leadToSave,
                agent: resolveLeadAgentForVisibility(leadToSave.agent ?? null, {
                    userId: user?.id,
                    canViewAllLeads,
                    defaultToCurrentUser: isNew,
                }),
            };

            if (isNew) {
                if (!leadPersist.contactId) {
                    throw new Error('Contato é obrigatório para criar negociação');
                }

                const created = await createLeadUseCase.execute({
                    name: leadPersist.name,
                    email: leadPersist.email,
                    whatsApp: leadPersist.whatsApp,
                    value: leadPersist.value,
                    stage: leadPersist.stage,
                    origin: leadPersist.origin,
                    agent: leadPersist.agent || undefined,
                    tags: leadPersist.tags,
                    observations: leadPersist.observations,
                    contactId: leadPersist.contactId,
                    organizationId,
                });

                recordCreation(created.id, user?.id);
                const leadForSync: Lead = {
                    ...leadPersist,
                    id: created.id,
                    createdBy: created.createdBy,
                    creatorName: created.creatorName,
                };
                void syncLeadToChat(leadForSync);
                await refreshLeads(meta?.refreshFunnelId);
                setLeads(prev => (prev.some(l => l.id === leadForSync.id) ? prev : [...prev, leadForSync]));
                return leadForSync;
            }

            await updateLeadUseCase.execute({
                id: leadPersist.id,
                name: leadPersist.name,
                email: leadPersist.email,
                whatsApp: leadPersist.whatsApp,
                value: leadPersist.value,
                stage: leadPersist.stage,
                status: leadPersist.status,
                origin: leadPersist.origin,
                agent: leadPersist.agent,
                tags: leadPersist.tags,
                observations: leadPersist.observations,
                contactId: leadPersist.contactId,
                organizationId,
            });

            afterLeadUpdated(leadPersist);
            await syncAgentToContactAndChat(leadPersist);
            trackWonIfNeeded(leadPersist, organizationId);
            await refreshLeads();
            return leadPersist;
        } catch (error) {
            ErrorHandler.handle(error as Error, 'SaveLead');
            return null;
        }
    }, [
        refreshLeads,
        recordCreation,
        afterLeadUpdated,
        organizationId,
        syncLeadToChat,
        syncAgentToContactAndChat,
        user?.id,
        canViewAllLeads,
    ]);

    const deleteLead = useCallback(async (leadId: string) => {
        try {
            await deleteLeadUseCase.execute(leadId);
            setLeads(prev => prev.filter(l => l.id !== leadId));
            if (organizationId) invalidateEnrichmentCache(organizationId);
        } catch (error) {
            ErrorHandler.handle(error as Error, 'DeleteLead');
        }
    }, [organizationId]);

    return { leads, setLeads, refreshLeads, updateLeadStage, saveLead, deleteLead };
};
